import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Bike, Plus, ArrowLeftRight, Trash2, MapPin, Loader2, Check, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;

const rentalBikes = (r) =>
  (r.bici && r.bici.length
    ? r.bici
    : [{ bike_id: r.bike_id, bike_numero: r.bike_numero, categoria_id: r.categoria_id, modello: "" }]
  ).map((b) => ({ bike_id: b.bike_id, numero: b.bike_numero || "", modello: b.modello || "", categoria_id: b.categoria_id || "", sede_nome: b.sede_nome }));

function CandidatePicker({ rental, bikes, categories, sedi, defaultCat, excludeIds, onPick, onClose }) {
  const [cat, setCat] = useState(defaultCat || (categories[0]?.id || ""));
  const [taglia, setTaglia] = useState("all");
  const [sede, setSede] = useState("all");
  const [loading, setLoading] = useState(false);
  const [candidate, setCandidate] = useState([]);

  const taglie = useMemo(
    () => [...new Set((bikes || []).map((b) => b.taglia).filter(Boolean))].sort(),
    [bikes]
  );

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!cat) { setCandidate([]); return; }
      setLoading(true);
      try {
        const params = { categoria_id: cat };
        if (taglia !== "all") params.taglia = taglia;
        if (sede !== "all") params.sede_id = sede;
        const res = await api.get(`/rentals/${rental.id}/bici-candidate`, { params });
        if (active) setCandidate((res.data.candidate || []).filter((c) => !excludeIds.includes(c.id)));
      } catch (e) {
        if (active) { setCandidate([]); toast.error(e.response?.data?.detail || "Errore nel caricamento candidate"); }
      } finally { if (active) setLoading(false); }
    };
    run();
    return () => { active = false; };
  }, [cat, taglia, sede, rental.id, JSON.stringify(excludeIds)]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#141414] border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="bike-candidate-picker">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2"><Bike className="h-5 w-5 text-[#f98125]" /> Scegli una bici disponibile</DialogTitle>
          <DialogDescription>Solo bici libere per il periodo/fascia della prenotazione (nessuna sovrapposizione, escluse manutenzione/fuori servizio/danno grave).</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2 py-1">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Categoria</Label>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid="cand-cat"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-zinc-800">{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Taglia</Label>
            <Select value={taglia} onValueChange={setTaglia}>
              <SelectTrigger className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid="cand-taglia"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-zinc-800"><SelectItem value="all">Tutte</SelectItem>{taglie.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Sede</Label>
            <Select value={sede} onValueChange={setSede}>
              <SelectTrigger className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid="cand-sede"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-zinc-800"><SelectItem value="all">Tutte</SelectItem>{sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5 py-2 min-h-[120px]">
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#f98125]" /></div>
          ) : candidate.length === 0 ? (
            <div className="py-8 text-center text-sm text-zinc-600" data-testid="cand-empty">Nessuna bici disponibile con questi filtri.</div>
          ) : candidate.map((c) => (
            <button key={c.id} onClick={() => onPick(c)} data-testid={`cand-bike-${c.id}`}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-sm border border-zinc-800 hover:border-[#f98125]/60 hover:bg-[#f98125]/5 text-left">
              <div>
                <div className="text-sm text-white font-medium">Bici {c.numero} <span className="text-zinc-500 font-normal">— {c.modello || "—"}</span></div>
                <div className="text-[11px] text-zinc-500 flex items-center gap-2">{c.taglia && <span>Taglia {c.taglia}</span>}{c.sede_nome && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{c.sede_nome}</span>}</div>
              </div>
              <Check className="h-4 w-4 text-[#f98125]" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BikeManager({ rental, bikes, categories, sedi, onChanged }) {
  const original = useMemo(() => rentalBikes(rental), [rental]);
  const [working, setWorking] = useState(original);
  const [picker, setPicker] = useState(null); // { mode:'add'|'replace', index }
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { old_total, new_total, old_caparra, new_caparra }

  useEffect(() => { setWorking(rentalBikes(rental)); }, [rental]);

  const workingIds = working.map((b) => b.bike_id);
  const dirty = JSON.stringify(workingIds) !== JSON.stringify(original.map((b) => b.bike_id));

  const removeAt = (i) => {
    if (working.length <= 1) { toast.error("La prenotazione deve avere almeno una bici"); return; }
    setWorking((w) => w.filter((_, idx) => idx !== i));
  };
  const openReplace = (i) => setPicker({ mode: "replace", index: i });
  const openAdd = () => setPicker({ mode: "add" });
  const onPick = (c) => {
    const entry = { bike_id: c.id, numero: c.numero, modello: c.modello, categoria_id: c.categoria_noleggio, sede_nome: c.sede_nome };
    if (picker.mode === "replace") setWorking((w) => w.map((b, idx) => (idx === picker.index ? entry : b)));
    else setWorking((w) => [...w, entry]);
    setPicker(null);
  };
  const resetChanges = () => setWorking(rentalBikes(rental));

  const apply = async (confirmPrice = false) => {
    setBusy(true);
    try {
      const res = await api.post(`/rentals/${rental.id}/sostituisci-bici`, {
        bici_finali: workingIds, confirm_price_change: confirmPrice,
      });
      if (res.data?.needs_confirmation) {
        setConfirm(res.data);
        setBusy(false);
        return;
      }
      toast.success("Bici della prenotazione aggiornate");
      setConfirm(null);
      onChanged?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Modifica non riuscita");
    } finally { setBusy(false); }
  };

  const defaultCat = picker?.mode === "replace"
    ? (working[picker.index]?.categoria_id || original[0]?.categoria_id)
    : (original[0]?.categoria_id);

  return (
    <div className="space-y-2" data-testid="bike-manager">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Bici prenotate ({working.length})</Label>
        {dirty && <button type="button" onClick={resetChanges} data-testid="bike-reset" className="text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1"><X className="h-3 w-3" /> Annulla modifiche</button>}
      </div>
      <div className="space-y-1.5" data-testid="bike-manager-list">
        {working.map((b, i) => {
          const isNew = !original.some((o) => o.bike_id === b.bike_id);
          return (
            <div key={`${b.bike_id}-${i}`} data-testid={`bike-row-${b.bike_id}`} className="flex items-center justify-between px-3 py-2 rounded-sm border border-zinc-800 bg-[#0A0A0A]">
              <div className="min-w-0">
                <div className="text-sm text-white font-medium flex items-center gap-2 truncate">
                  <Bike className="h-4 w-4 text-[#f98125] shrink-0" /> Bici {b.numero} <span className="text-zinc-500 font-normal truncate">— {b.modello || "—"}</span>
                  {isNew && <span className="text-[9px] uppercase tracking-wider bg-[#39FF14]/20 text-[#39FF14] px-1.5 py-0.5 rounded-sm shrink-0">nuova</span>}
                </div>
                {b.sede_nome && <div className="text-[11px] text-zinc-500 flex items-center gap-0.5"><MapPin className="h-3 w-3" />{b.sede_nome}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" onClick={() => openReplace(i)} data-testid={`bike-replace-${b.bike_id}`} title="Sostituisci con bici disponibile" className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-sm border border-zinc-800 text-zinc-300 hover:border-[#f98125]/60 hover:text-[#f98125]"><ArrowLeftRight className="h-3.5 w-3.5" /> Sostituisci</button>
                <button type="button" onClick={() => removeAt(i)} disabled={working.length <= 1} data-testid={`bike-remove-${b.bike_id}`} title="Rimuovi dalla prenotazione" className="p-1.5 rounded-sm border border-zinc-800 text-zinc-500 hover:border-[#FF3B30]/60 hover:text-[#FF6B60] disabled:opacity-30 disabled:cursor-not-allowed"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={openAdd} data-testid="bike-add" className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-sm border border-dashed border-[#f98125]/50 text-[#f98125] hover:bg-[#f98125]/10"><Plus className="h-4 w-4" /> Aggiungi bici</button>
        <button type="button" onClick={() => apply(false)} disabled={!dirty || busy} data-testid="bike-apply" className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-sm bg-[#f98125] text-white font-bold hover:bg-[#d96a12] disabled:opacity-40 disabled:cursor-not-allowed">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Applica modifiche bici</button>
      </div>
      <p className="text-[11px] text-zinc-600">Sostituendo/aggiungendo/rimuovendo bici, prezzo e caparra vengono ricalcolati e la modifica registrata nello storico.</p>

      {picker && (
        <CandidatePicker rental={rental} bikes={bikes} categories={categories} sedi={sedi}
          defaultCat={defaultCat} excludeIds={workingIds} onPick={onPick} onClose={() => setPicker(null)} />
      )}

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent className="bg-[#141414] border-zinc-800" data-testid="bike-price-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma variazione di prezzo</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-1 text-sm">
                <div>La modifica delle bici cambia il totale della prenotazione.</div>
                {confirm && (
                  <div className="mt-2 rounded-sm border border-zinc-800 p-2 space-y-1">
                    <div className="flex justify-between"><span className="text-zinc-500">Totale</span><span>{eur(confirm.old_total)} → <b className="text-[#f98125]">{eur(confirm.new_total)}</b></span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Caparra</span><span>{eur(confirm.old_caparra)} → <b className="text-[#f98125]">{eur(confirm.new_caparra)}</b></span></div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bike-price-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="bike-price-ok" onClick={() => apply(true)} className="bg-[#f98125] hover:bg-[#d96a12]">Conferma e applica</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
